import {useEffect, useMemo, useRef, useState} from 'react'
import {useLayout} from '../../../_metronic/layout/core'
import {KTCard, KTCardBody, KTIcon, useDebounce} from '../../../_metronic/helpers'
import {
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import toast from 'react-hot-toast'
import {IProfitAndLoss, getProfitAndLossColumns} from './columns'
import {CardsWidget7} from '../../../_metronic/partials/widgets'
import {getPLReport, Ledger, subledger} from './api'
import * as XLSX from 'xlsx'
import {Loader} from '../product-master/loader'
import {formatNumber} from '../../../_metronic/helpers'
import {useNavigate} from 'react-router-dom'

// Helper function to transform API response to expected table format
const processCategory = (categoryData: any, classification: string): IProfitAndLoss => {
  // Root category
  const result: IProfitAndLoss = {
    groupId: categoryData.groupId,
    classification,
    amount: categoryData.amount,
    children: [],
  }

  // Process each parent ledger
  if (categoryData.children && categoryData.children.length > 0) {
    result.children = categoryData.children.map((parentItem: Ledger) => {
      // console.log(parentItem)
      // Parent ledger level
      const parentLedgerItem: IProfitAndLoss = {
        classification: '',
        ledgerId: parentItem.ledgerId,
        parentLedger: parentItem.parentLedger,
        amount: +parentItem.amount,
        children: [],
      }

      // Process subledgers if they exist
      if (parentItem.children && parentItem.children.length > 0) {
        parentLedgerItem.children = parentItem.children.map((subItem: subledger) => {
          // Subledger level
          return {
            classification: '',
            parentLedger: parentItem.parentLedger,
            subLedger: subItem.subLedger || null,
            amount: subItem.amount || 0,
            ledgerId: subItem.ledgerId,
          }
        })
      }

      return parentLedgerItem
    })
  }
  // console.log(result)
  return result
}

// Helper function to set all rows expanded
const expandAllRows = (data: IProfitAndLoss[]) => {
  const newExpanded: ExpandedState = {}

  // Recursive function to expand all rows
  const expandRow = (row: IProfitAndLoss, path: string) => {
    if (row.children && row.children.length > 0) {
      newExpanded[path] = true
      row.children.forEach((child, index) => {
        expandRow(child, `${path}.${index}`)
      })
    }
  }

  // Expand all top-level rows and their children
  data.forEach((row, index) => {
    expandRow(row, index.toString())
  })

  return newExpanded
}

export const ProfitAndLoss = () => {
  const {company, financialYear, fyEndDate, fyStartDate} = useLayout()
  const [data1, setData1] = useState<IProfitAndLoss[]>([])
  const [data2, setData2] = useState<IProfitAndLoss[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isError, setIsError] = useState(false)
  const [from, setFrom] = useState<string>('')
  const [to, setTo] = useState<string>('')
  const [isDateUpdate, setIsDateUpdate] = useState(false)
  const [totals, setTotals] = useState<{
    grossProfit: number | null
    grossLoss: number | null
    netProfit: number | null
    netLoss: number | null
  }>({
    grossProfit: 0,
    grossLoss: 0,
    netProfit: 0,
    netLoss: 0,
  })
  const [isButtonHovered, setIsButtonHovered] = useState(false)
  // // console.log('render')
  // const debouncedFrom = useDebounce(from, 1000)
  // const debouncedTo = useDebounce(to, 1000)

  const navigate = useNavigate()
  useEffect(() => {
    if (!company || !company.value || !financialYear || !financialYear.value) {
      navigate('/companies')
    }
  }, [company.value, financialYear.value])

  useEffect(() => {
    if (fyStartDate && fyEndDate) {
      setFrom(fyStartDate)
      setTo(fyEndDate)
    } else {
      const storedFromDate = localStorage.getItem('fromDate')
      const storedToDate = localStorage.getItem('toDate')
      if (!storedFromDate || !storedToDate) {
        if (!financialYear || !financialYear.value) return
        const [year1, year2] = financialYear?.value.split('-')
        setFrom(new Date(Number(year1.trim()), 3, 2).toISOString().split('T')[0])
        setTo(new Date(Number(year2.trim()), 3, 1).toISOString().split('T')[0])
      } else {
        setFrom(storedFromDate)
        setTo(storedToDate)
      }
    }
    setIsDateUpdate(true)
  }, [fyStartDate, fyEndDate])

  useEffect(() => {
    if (isDateUpdate) {
      localStorage.setItem('fromDate', from)
      localStorage.setItem('toDate', to)
    }
  }, [isDateUpdate])

  useEffect(() => {
    const getData = async () => {
      if (company?.value && financialYear?.value && from && to && isDateUpdate) {
        try {
          setIsError(false)
          setIsLoading(true)
          const res = await getPLReport({
            company_id: Number(company.value),
            fy_name: financialYear.value,
            from_date: from,
            to_date: to,
          })
          // console.log(res)
          if (res) {
            setTotals({
              grossProfit: res?.grossProfit,
              grossLoss: res?.grossLoss,
              netProfit: res?.netProfit,
              netLoss: res?.netLoss,
            })

            setData1([
              processCategory(res['Direct Income'], 'Direct Income'),
              processCategory(res['Direct Expenses'], 'Direct Expenses'),
            ])

            setData2([
              processCategory(res['Indirect Income'], 'Indirect Income'),
              processCategory(res['Indirect Expenses'], 'Indirect Expenses'),
            ])
          }
        } catch (err: any) {
          toast.error(err.message || 'Some Error Occured')
          setData1([])
          setData2([])
          setTotals({
            grossProfit: 0,
            grossLoss: 0,
            netProfit: 0,
            netLoss: 0,
          })
          setIsError(true)
        } finally {
          setIsLoading(false)
          setIsDateUpdate(false)
        }
      }
    }
    getData()
  }, [company?.value, from, to, financialYear?.value, isDateUpdate])

  const columns = useMemo(() => getProfitAndLossColumns(from, to), [from, to])

  const [expanded, setExpanded] = useState<ExpandedState>({
    '0': true,
    '1': true,
    '2': true,
    '3': true,
  })

  // Update expanded state whenever data changes
  useEffect(() => {
    if (data1.length > 0) {
      const expandedState = {...expandAllRows(data1), ...expandAllRows(data2)}
      setExpanded(expandedState)
    }
  }, [data1, data2])
  const table1 = useReactTable({
    columns,
    data: data1,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => !!row.original.children?.length,
  })
  const table2 = useReactTable({
    columns,
    data: data2,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => !!row.original.children?.length,
  })
  const tableRef1 = useRef(null)
  const tableRef2 = useRef(null)
  const handleDownload = () => {
    toast.success('Download has been started successfully')

    // Get data from tables
    const data1 = tableRef1.current
    const data2 = tableRef2.current

    // Create worksheets for each table
    const ws1 = XLSX.utils.table_to_sheet(data1)
    const ws2 = XLSX.utils.table_to_sheet(data2)

    // Combine data into a single worksheet
    const combinedData = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(combinedData, ws1, 'Direct Expense')
    XLSX.utils.book_append_sheet(combinedData, ws2, 'Indirect Expense')

    // Save the file
    const fileName = `Profit-loss${company.label}_${financialYear.value}.xlsx`
    XLSX.writeFile(combinedData, fileName)
  }

  return (
    <>
      <KTCard className='mb-5'>
        <div className='card-header border-0'>
          <div className='card-title'>
            {/* begin::Search */}
            <div className='d-flex align-items-center position-relative my-1'>
              {/* <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' /> */}
              <input
                type='date'
                data-kt-user-table-filter='search'
                className='form-control form-control-solid w-250px ps-14 me-3'
                placeholder='Search'
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <input
                type='date'
                data-kt-user-table-filter='search'
                className='form-control form-control-solid w-250px ps-14 ms-3'
                placeholder='Search'
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
              <button
                type='button'
                className='btn btn-lg ms-3'
                style={{
                  borderRadius: '1000%',
                  height: '30px',
                  width: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  backgroundColor: !isButtonHovered ? '#f0f0f0' : '#b0b0b0',
                }}
                onMouseEnter={() => setIsButtonHovered(true)}
                onMouseLeave={() => setIsButtonHovered(false)}
                onClick={() => {
                  if (!from || !to)
                    toast.error('Please make sure both the From and To dates are selected')
                  else setIsDateUpdate(true)
                }}
              >
                <KTIcon iconName='arrows-circle' className='fs-3 px-0' />
              </button>
            </div>
            {/* end::Search */}
          </div>
          {/* begin::Card toolbar */}
          <div className='card-toolbar'>
            {/* begin::Group actions */}
            {
              <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
                {/* begin::Add user */}
                <button
                  disabled={!data1.length || !data2.length}
                  type='button'
                  onClick={handleDownload}
                  className='btn p-0'
                  style={{
                    backgroundColor: 'var(--bs-primary)',
                    color: 'var(--bs-primary-inverse)',
                    display: 'inline-flex',
                    height: '43.59px',
                    alignItems: 'center',
                    justifyContent: 'space-between',

                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <span className='px-5'>Download</span>
                  <span
                    style={{
                      color: '#fff',
                      backgroundColor: '#006060',
                      padding: '0 15px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      borderTopRightRadius: 'inherit',
                      borderBottomRightRadius: 'inherit',
                    }}
                  >
                    <KTIcon iconName='arrow-down' className='fs-2 text-white' />
                  </span>
                </button>
                {/* end::Add user */}
              </div>
            }
            {/* end::Group actions */}
          </div>
          {/* end::Card toolbar */}
        </div>
      </KTCard>
      <KTCard>
        <KTCardBody className={`py-4 ${from && to && isDateUpdate ? 'position-relative' : ''}`}>
          {!isLoading && (
            <>
              <div className='d-flex justify-content-start mb-4'>
                <div className='btn-group'>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => {
                      table1.toggleAllRowsExpanded(true)
                      table2.toggleAllRowsExpanded(true)
                    }}
                    title='Expand All'
                  >
                    <KTIcon iconName='double-down' className='fs-3 me-1' />
                    Expand All
                  </button>
                  <button
                    type='button'
                    className='btn btn-sm btn-light-primary'
                    onClick={() => {
                      table1.toggleAllRowsExpanded(false)
                      table2.toggleAllRowsExpanded(false)
                    }}
                    title='Collapse All'
                  >
                    <KTIcon iconName='double-up' className='fs-3 me-1' />
                    Collapse All
                  </button>
                </div>
              </div>
              <div className='table-responsive'>
                <table
                  ref={tableRef1}
                  className='table align-middle fs-6  dataTable'
                  style={{
                    tableLayout: 'fixed',
                  }}
                >
                  {data1.length > 0 ? (
                    <>
                      <thead>
                        {table1.getHeaderGroups().map((headerGroup) => (
                          <tr
                            key={headerGroup.id}
                            className='text-start text-muted fw-bolder bg-light ps-4 fs-7 text-uppercase gs-0'
                          >
                            {headerGroup.headers.map((header) => (
                              <th
                                key={header.id}
                                colSpan={header.colSpan}
                                style={{
                                  border: '1px solid #e1e3ea',
                                  textAlign: 'center',
                                }}
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(header.column.columnDef.header, header.getContext())}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody className='text-gray-600 fw-bold'>
                        {table1.getRowModel().rows.map((row) => (
                          <>
                            <tr
                              key={row.id}
                              onClick={
                                row.depth > 0 || row.getCanExpand()
                                  ? row.getToggleExpandedHandler()
                                  : undefined
                              }
                              className='bg-hover-light'
                            >
                              {row.getVisibleCells().length > 0 ? (
                                row
                                  .getVisibleCells()
                                  .map((cell) => (
                                    <td key={cell.id}>
                                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                  ))
                              ) : (
                                <td colSpan={7}>
                                  <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                                    No matching records found
                                  </div>
                                </td>
                              )}
                            </tr>
                          </>
                        ))}
                      </tbody>
                      <tfoot></tfoot>
                    </>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={7}>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                            No matching records found
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
                {/* <div className='d-flex flex-start' style={{gap: '10px'}}>
                  {totals.grossProfit !== null && totals.grossProfit >= 0 && (
                    <CardsWidget7
                      className='border border-dashed border-gray-300'
                      stats={String(totals.grossProfit)}
                      description='Gross Profit'
                    />
                  )}
                  {totals.grossLoss !== null && totals.grossLoss < 0 && (
                    <CardsWidget7
                      className='border border-dashed border-gray-300'
                      stats={String(totals.grossLoss)}
                      description='Gross Loss'
                    />
                  )}
                </div> */}
                <div className='bg-light d-flex justify-content-between text-dark py-5'>
                  {totals.grossProfit !== null && totals.grossProfit >= 0 && (
                    <>
                      <span
                        className='col-2 d-flex justify-content-start px-3 text-primary'
                        style={{fontSize: '12px', fontWeight: '600'}}
                      >
                        Gross Profit
                      </span>
                      <span style={{fontSize: '14px', fontWeight: '800'}}>
                        {/* {currency} {formatNumber(totals.grossProfit)} */}
                        {formatNumber(totals.grossProfit, undefined, 1, 1)}
                      </span>
                    </>
                  )}
                  {totals.grossLoss !== null && totals.grossLoss < 0 && (
                    <>
                      <span
                        className='col-2 d-flex justify-content-start px-3 text-primary'
                        style={{fontSize: '12px', fontWeight: '600'}}
                      >
                        Gross Loss
                      </span>
                      <span style={{fontSize: '14px', fontWeight: '800'}}>
                        {/* {currency} {formatNumber(totals.grossProfit)} */}
                        {formatNumber(totals.grossLoss, 'en-us', 1, 1)}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className='table-responsive'>
                <table
                  className='table align-middle fs-6  dataTable '
                  ref={tableRef2}
                  style={{tableLayout: 'fixed'}}
                >
                  {data2.length > 0 ? (
                    <>
                      <tbody className='text-gray-600 fw-bold'>
                        {table2.getRowModel().rows.map((row) => (
                          <tr
                            key={row.id}
                            onClick={
                              row.getCanExpand() ? row.getToggleExpandedHandler() : undefined
                            }
                            className='bg-hover-light'
                          >
                            {row.getVisibleCells().length > 0 ? (
                              row
                                .getVisibleCells()
                                .map((cell) => (
                                  <td key={cell.id}>
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                  </td>
                                ))
                            ) : (
                              <td colSpan={7}>
                                <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                                  No matching records found
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot></tfoot>
                    </>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={7}>
                          <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                            No matching records found
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )}
                </table>
                <div className='bg-light d-flex justify-content-between text-dark fw-bolder py-5'>
                  {totals.netProfit !== null && totals.netProfit >= 0 && (
                    <>
                      <span
                        className='col-2 d-flex justify-content-start px-3 text-primary'
                        style={{fontSize: '12px', fontWeight: '600'}}
                      >
                        Net Profit
                      </span>
                      <span style={{fontSize: '14px', fontWeight: '800'}}>
                        {/* {currency} {formatNumber(totals.grossProfit)} */}
                        {formatNumber(totals.netProfit, 'en-us', 1, 1)}
                      </span>
                    </>
                  )}
                  {totals.netLoss !== null && totals.netLoss < 0 && (
                    <>
                      <span
                        className='col-2 d-flex justify-content-start px-3 text-primary'
                        style={{fontSize: '12px', fontWeight: '600'}}
                      >
                        Net Loss
                      </span>
                      <span style={{fontSize: '14px', fontWeight: '800'}}>
                        {/* {currency} {formatNumber(totals.grossProfit)} */}
                        {formatNumber(totals.netLoss, 'en-us', 1, 1)}
                      </span>
                    </>
                  )}
                </div>
                {/* <div className='d-flex flex-start' style={{gap: '10px'}}>
                  {totals.netProfit !== null && totals.netProfit >= 0 && (
                    <CardsWidget7
                      className='border border-dashed border-gray-300'
                      stats={String(totals.netProfit)}
                      description='Net Profit'
                    />
                  )}
                  {totals.netLoss !== null && totals.netLoss < 0 && (
                    <CardsWidget7
                      className='border border-dashed border-gray-300'
                      stats={String(totals.netLoss)}
                      description='Net Loss'
                    />
                  )}
                </div> */}
              </div>
            </>
          )}
          {isLoading && <Loader isError={isError} />}
        </KTCardBody>
      </KTCard>
    </>
  )
}

const styles = {
  borderRadius: '0.475rem',
  boxShadow: '0 0 50px 0 rgb(82 63 105 / 15%)',
  backgroundColor: '#fff',
  color: '#7e8299',
  fontWeight: '500',
  margin: '0',
  width: 'auto',
  padding: '1rem 2rem',
  top: 'calc(50% - 2rem)',
  left: 'calc(50% - 4rem)',
}
